import { quantityStepError, type ProductUnitFields } from '@/lib/product-units';
import type { DealProduct } from '@/lib/types';

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * Editing a deal's line items ON THE WAY INTO A QUOTE **OR AN INVOICE**.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `POST /deals/{id}/convert` copies every line of a deal onto a new Draft
 * quote, inside the transaction that creates it.
 *
 * ⚠️ **CORRECTED — IT TAKES TWO OPTIONAL KEYS NOW, AND THEY ARE MUTUALLY
 * EXCLUSIVE.** *SUPERSEDED: "It now takes ONE optional key — `lines` — carrying
 * a per-line override for the four figures and the wording a person may
 * reasonably want to change while they are looking at them."* That describes
 * `lines`, which is unchanged and is still the whole story on
 * `POST /deals/{id}/invoices`. The quote route also takes **`lineSet`** — the
 * COMPLETE FINAL list of lines, which can change a line's PRODUCT, PROJECT and
 * CITIES, ADD lines the deal never had and REMOVE lines it has. Everything from
 * here to {@link convertLineFieldErrors} is about `lines`; the `lineSet` model
 * is the section at the foot of this file.
 *
 * ⚠️ **`POST /deals/{id}/invoices` TAKES THE IDENTICAL KEY**, added the same
 * day and measured on the wire — same per-line shape, same `dealLineId`
 * identity, same `.strict()`, same max of 200, same indexed refusal paths. This
 * module is therefore **one model for two routes** rather than two identical
 * ones: a second copy would agree on the day it was written and drift on the
 * first correction, and what would drift is the discount pair — the one thing
 * here whose two halves are mutually exclusive columns on a customer-facing
 * document.
 *
 * ⚠️ **ON BOTH ROUTES THE EDITS APPLY TO THE NEW DOCUMENT ONLY; THE DEAL IS
 * NEVER WRITTEN.** Verified on the wire. Nothing here may be presented as
 * correcting the deal's own basket — that is the deal's own line editor. (The
 * deal's STAGE does move since 2026-10-18 — to quote-sent or won, server-side —
 * but its lines never do.)
 *
 * ⚠️ **An override naming a line that is not on the deal is refused BEFORE the
 * invoice route's `COMPANY_HAS_NO_TAX_RATE` check**, so on that route a bad
 * `dealLineId` and a company with no tax rate cannot be met in one response.
 * Route the line refusal first; see {@link convertLineFieldErrors}.
 *
 * ⚠️ **OMIT `lines` ENTIRELY WHEN NOTHING WAS EDITED.** An absent key is
 * today's exact behaviour, which is the behaviour every converted quote has had
 * until now; an empty array is a different instruction and is not what "nothing
 * changed" means. {@link convertLineOverrides} returns `[]` and the caller
 * omits the key on a length of zero, so the two can never be confused.
 *
 * ## The FOUR things this module knows, and why they live together
 *
 * Only `dealLineId` is required per entry, so a request says exactly what
 * somebody touched and nothing else. Working out *what was touched* needs the
 * seed, the draft and the discount pair resolved as one — three facts that give
 * a wrong answer independently — so the seed, the change test, the validator
 * and the payload builder are one module rather than four call-site decisions.
 *
 * ## ⚠️ THE DISCOUNT IS ONE CONTROL WITH A MODE, NEVER TWO BOXES
 *
 * `discount` is a PERCENTAGE and `discountAmount` an exact sum, and **the
 * database refuses a row stating both** — sending both non-zero is a 422 naming
 * `discountAmount`. Two independent boxes let somebody fill both and produce a
 * refusal they cannot read, so the draft carries the MODE (`discountAmount ===
 * null` is percentage mode, a string is amount mode) and
 * {@link convertDiscountWrite} turns the mode into the pair the wire carries.
 * That is the same resolver shape the three saved line grids use, deliberately.
 *
 * ⚠️ **There is NO `taxRate` key and sending one is a 422** — the body schema is
 * `.strict()`, and `f_deal_products` has no tax column at all. The owner's
 * instruction is that the tax part specifically stays uneditable here.
 *
 * ## Why this is a `src/lib/` module and not a block inside the modal
 *
 * `QuoteFormModal` is a component module, and oxlint's
 * `react(only-export-components)` fires when one exports plain functions — the
 * same mechanical reason `src/lib/document-validation.ts` exists. The house
 * rule that shared rules live in `src/lib/` (`inclusions.ts`,
 * `line-project.ts`, `deal-conversion.ts`, `document-validation.ts`) is the
 * other half.
 *
 * ## ⚠️ THE TWO PATTERNS BELOW ARE A FOURTH COPY, AND THAT IS A KNOWN COST
 *
 * `QuoteLineItemsTable`, `InvoiceLineItemsTable` and `DealLineItemsTable` each
 * declare the identical ten-digit price shape and hundred-bounded percentage
 * shape privately, and none exports them — a component module cannot, for the
 * oxlint reason above. Lifting all four into here is the right end state and is
 * a change to three files this pass does not own. **Until somebody does it,
 * three copies agree only until one of them is edited, and the one that drifts
 * is always the one nobody was looking at.**
 */

/**
 * ⛔ **ONE PATTERN BECAME TWO ON 2026-09-12, AND MERGING THEM AGAIN IS A
 * SURCHARGE.** This was a single `PRICE_PATTERN` serving `unitPrice` AND
 * `discountAmount` — correct while both were non-negative, and wrong the moment
 * one of them stopped being.
 *
 * Migration 171 lets a line PRICE be negative (a deduction line), while
 * `discountAmount` stays non-negative, enforced by `ck_*_discount_amount_sign`.
 * So a negative in the first box is a credit and a negative in the second is a
 * **surcharge arriving through the discount field** — a wrong number on a
 * customer's document, refused by the database only as errno 4025, which is a
 * 422 naming no field at all and which the person cannot bind to the control
 * they typed in.
 *
 * The backend pins this pair behaviourally in
 * `tests/line-negative-unit-price.test.ts`; this is the client half of the same
 * rule. **They differ by one character and must never converge.**
 */

/** `unitPrice`: `^-?\d{1,10}(\.\d{1,2})?$` — SIGNED since migration 171. */
const PRICE_PATTERN = /^-?\d{1,10}(\.\d{1,2})?$/;

/** `discountAmount`: unsigned, matching the CHECK constraint on all three tables. */
const DISCOUNT_AMOUNT_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;
/** `discount`: a PERCENTAGE 0..100. */
const PERCENT_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;

/**
 * `description` caps here.
 *
 * Checked before sending rather than enforced with a `maxLength` on the box: a
 * `maxLength` truncates a PASTE silently, and the tail it drops is wording
 * somebody meant to keep.
 */
const MAX_DESCRIPTION_LENGTH = 16000;

/** The five keys a row can be marked on, and the only five. */
export type ConvertLineField =
  | 'quantity'
  | 'unitPrice'
  | 'discount'
  | 'discountAmount'
  | 'description';

/**
 * One line's override, exactly as the wire carries it.
 *
 * ⚠️ **Money is a STRING and never a number.** `quantity` is the one real JSON
 * number here — the schema does not coerce, so a quoted quantity is a 422 on it
 * and a bare number is a 422 on the other three.
 */
export interface DealConvertLineOverride {
  /**
   * ⚠️ **`f_deal_products.id` — the LINE's id, never the deal's.** It must be
   * one of that deal's own lines; anything else is a 422 at
   * `lines.<n>.dealLineId`, and so is the same id twice.
   */
  dealLineId: number;
  quantity?: number;
  unitPrice?: string;
  /** A percentage, as typed. Travels with {@link DealConvertLineOverride.discountAmount}, always. */
  discount?: string;
  /** An exact sum, or `null` for "this line discounts by percentage". */
  discountAmount?: string | null;
  /** Verbatim wording, or `null` to go back to DERIVING it server-side. */
  description?: string | null;
  /**
   * The line's price tier (migration 328), or `null` for the default tier.
   * Absent copies the deal line's tier. Copying a tier never re-prices a line.
   */
  priceTierId?: number | null;
}

/**
 * The body of `POST /deals/{id}/convert` **and of `POST /deals/{id}/invoices`**
 * — the same shape on both, deliberately one type. Every key optional; an empty
 * object is valid and is what a caller with nothing to override sends.
 *
 * ⚠️ **`{}`, never an absent body.** Both schemas are `.strict()` and still
 * expect a body; `request()` omits `Content-Type` when there is none, which has
 * already broken one convert endpoint for every user in this app's history.
 *
 * ⚠️ **`lines` AND `lineSet` ARE MUTUALLY EXCLUSIVE — sending BOTH is a 422.**
 * They are two optional keys rather than one, and they carry OPPOSITE
 * instructions about the rows nobody named: see the `lineSet` section at the
 * foot of this file for what that buys and why the split is a deploy-safety
 * property rather than a tidiness one. `lines` is what
 * `POST /deals/{id}/invoices` sends; `lineSet` is quote-convert only today.
 */
export interface DealConvertInput {
  /**
   * ⚠️ **Omitted when nothing was edited.** An empty array is a different
   * instruction — and note this is NOT the clearing `[]` of `cityIds` or
   * `inclusions`: `lines: []` means "respell none", not "remove the lines".
   * Omitting is what every conversion did before overrides existed.
   */
  lines?: DealConvertLineOverride[];
  /**
   * The COMPLETE, FINAL list of lines for the new quote.
   *
   * ⚠️ **A line omitted from this array DOES NOT APPEAR on the quote** — the
   * exact opposite of an omission from `lines`, which is still copied. Omit the
   * key entirely to copy every deal line unchanged; `lineSet: []` is a quote
   * with no lines at all, which is a real instruction and never "no changes".
   *
   * ⚠️ **Never send this alongside `lines`.** See {@link DealConvertInput}.
   */
  lineSet?: DealConvertLineSetEntry[];
}

/**
 * What one row holds while somebody is editing it — strings, as typed.
 *
 * `discountAmount` is `null` in percentage mode and a string in amount mode,
 * which is the same discriminator the saved line grids use and the same one
 * {@link convertDiscountWrite} reads.
 */
export interface ConvertLineEdit {
  quantity: string;
  /** Empty means the line records no price. */
  unitPrice: string;
  discount: string;
  discountAmount: string | null;
  /** Empty means the wording is DERIVED, which is what `null` means on the row. */
  description: string;
}

/**
 * The five stored values this module reads off a line, whatever document the
 * line belongs to.
 *
 * ⚠️ **STRUCTURAL ON PURPOSE, so `QuoteProduct` may use this module without a
 * second copy of it.** `POST /quotes/{id}/convert` gained the identical `lines`
 * key on 2026-08-31, keyed `quoteProductId` instead of `dealLineId` — the
 * backend's own schema comment calls the two "two paths, one grammar". A
 * `DealProduct`, a `QuoteProduct` and an `InvoiceProduct` all satisfy this
 * shape, and the header's warning about a second copy drifting applies with
 * full force to the discount pair below: `@/lib/quote-convert-lines` therefore
 * imports {@link convertLineChanges} rather than restating it.
 */
export interface ConvertibleLineValues {
  quantity: number;
  unitPrice: string | null;
  discount: string;
  discountAmount: string | null;
  description: string | null;
}

/**
 * The row's stored values, as the boxes start.
 *
 * ⚠️ **This is the BASELINE the change test compares against**, so it must be
 * the line's own values and never a default. Seeding a blank box and treating
 * "not blank" as edited would send every figure on every row — including the
 * ones nobody touched — and a request that restates a value is a request that
 * can be refused for a value the person never chose.
 */
export function convertLineEditOf(line: ConvertibleLineValues): ConvertLineEdit {
  return {
    quantity: String(line.quantity),
    unitPrice: line.unitPrice ?? '',
    discount: line.discount,
    discountAmount: line.discountAmount,
    description: line.description ?? '',
  };
}

/** Field-for-field, so a mode flip counts as a change on its own. */
export function convertLineEditsMatch(a: ConvertLineEdit, b: ConvertLineEdit): boolean {
  return (
    a.quantity === b.quantity &&
    a.unitPrice === b.unitPrice &&
    a.discount === b.discount &&
    a.discountAmount === b.discountAmount &&
    a.description === b.description
  );
}

/** What one draft's discount MEANS on the wire — the pair, resolved once. */
interface ConvertDiscountWrite {
  discount: string;
  discountAmount: string | null;
}

/**
 * ⚠️ **The pair travels together whenever either half moves**, because the
 * server refuses two live discounts: setting one while the other stays stored
 * IS the refusal.
 *
 * ⚠️ **An emptied AMOUNT box is a real zero, not a blank.** The percentage
 * control is not on screen in that mode, so "no amount" can only mean "no
 * discount on this line" — leaving a stored percentage standing would show an
 * empty discount box over a line that is still discounted.
 *
 * A blank PERCENTAGE box is the server's default of zero, which is the same
 * reading `GeneratedLineInput.discount` states in `@/lib/decimal`. It is a
 * meaning, not a conversion: nothing here computes one figure from another.
 */
export function convertDiscountWrite(edit: ConvertLineEdit): ConvertDiscountWrite {
  if (edit.discountAmount === null) {
    const percentage = edit.discount.trim();
    return { discount: percentage === '' ? '0' : percentage, discountAmount: null };
  }
  const amount = edit.discountAmount.trim();
  return { discount: '0', discountAmount: amount === '' ? null : amount };
}

/**
 * Everything this row would be refused for, marked on the control that caused
 * it.
 *
 * The sentences are the SAME ONES `DealLineItemsTable` uses on the identical
 * columns, verbatim. A person told "A percentage between 0 and 100" on the deal
 * and something else on the conversion has no way to know they were refused for
 * one reason.
 *
 * ⚠️ **ONE DELIBERATE EXCEPTION, recorded rather than left to be discovered: a
 * NEGATIVE `discountAmount`.** This branch answers *"A discount cannot be
 * negative. To reduce a line below zero, use a negative price."*; the grid
 * answers its generic format sentence for every malformed amount alike. So the
 * "verbatim" claim above is true of four sentences out of five as of
 * 2026-09-12. The divergence is in the SAFE direction — this one names the trap
 * migration 171 opened — and closing it means the GRID adopting this sentence,
 * never this file dropping it.
 *
 * ⚠️ **Exactly one half of the discount is checked, because exactly one is on
 * screen.** Reporting the other's format would name a box that is not there.
 */
export function validateConvertLineEdit(
  edit: ConvertLineEdit,
  seed: ConvertLineEdit,
  /**
   * The line's product, for its `quantityStep` (`@/lib/product-units`).
   * Optional, so a caller that does not pass it judges no step — the server's
   * 422 `QUANTITY_NOT_MULTIPLE` still stands behind it.
   */
  units?: ProductUnitFields | null,
): Partial<Record<ConvertLineField, string>> {
  const errors: Partial<Record<ConvertLineField, string>> = {};

  /*
   * ⚠️ Blank is refused rather than read as "leave it alone". Every box here
   * starts filled from the line, so an empty one is somebody having cleared a
   * figure the quote cannot do without — and `quantity` is NOT NULL.
   */
  const quantity = Number(edit.quantity.trim());
  if (edit.quantity.trim() === '' || !Number.isInteger(quantity) || quantity < 1) {
    errors.quantity = 'A whole number of 1 or more.';
  } else if (edit.quantity !== seed.quantity) {
    /*
     * Only a CHANGED quantity is judged against the step: an unchanged one is
     * not sent, and a legacy line from before the step existed keeps working.
     */
    const stepError = quantityStepError(units, edit.quantity);
    if (stepError !== null) errors.quantity = stepError;
  }

  const price = edit.unitPrice.trim();
  if (price === '') {
    /*
     * ⚠️ Only where the line HAD a price. A deal line may legitimately record
     * none, and refusing an already-empty box would make such a row permanently
     * unconvertible over a figure nobody removed.
     *
     * ⚠️ There is no "record this line as having none" control here and no
     * `unitPrice: null` in the override contract, so a price that exists cannot
     * be cleared on this screen. The line's own editor on the deal is where
     * that is done.
     */
    if (seed.unitPrice !== '') errors.unitPrice = 'Enter a price.';
  } else if (!PRICE_PATTERN.test(price)) {
    errors.unitPrice = 'Up to two decimal places, and no currency symbol.';
  }

  if (edit.discountAmount === null) {
    const discount = edit.discount.trim();
    // `Number()` here compares a bound, never money. The value itself is sent
    // as the string the user typed, untouched by a float.
    if (discount !== '' && (!PERCENT_PATTERN.test(discount) || Number(discount) > 100)) {
      errors.discount = 'A percentage between 0 and 100.';
    }
  } else {
    const amount = edit.discountAmount.trim();
    // ⛔ DISCOUNT_AMOUNT_PATTERN, not PRICE_PATTERN — see the block at both
    // declarations. A negative here is a surcharge, not a credit.
    if (amount !== '' && !DISCOUNT_AMOUNT_PATTERN.test(amount)) {
      errors.discountAmount = amount.startsWith('-')
        ? 'A discount cannot be negative. To reduce a line below zero, use a negative price.'
        : 'An amount, up to two decimal places and with no currency symbol.';
    }
  }

  if (edit.description.length > MAX_DESCRIPTION_LENGTH) {
    errors.description = `At most ${MAX_DESCRIPTION_LENGTH.toLocaleString()} characters — this is ${edit.description.length.toLocaleString()}.`;
  }

  return errors;
}

/**
 * One line's override, or `null` when nothing on that row moved.
 *
 * ⚠️ **Only what CHANGED is sent.** A key restating a stored value is a key the
 * server can refuse for something the person never chose, and it makes the
 * request unreadable to anyone trying to work out afterwards what a conversion
 * actually did.
 */
export function convertLineOverrideOf(
  dealLineId: number,
  edit: ConvertLineEdit,
  seed: ConvertLineEdit,
): DealConvertLineOverride | null {
  const changes = convertLineChanges(edit, seed);
  return changes === null ? null : { dealLineId, ...changes };
}

/**
 * The changed FIGURES of one row, with no identity key on them — or `null` when
 * nothing moved.
 *
 * ⚠️ **EXTRACTED SO THE DISCOUNT PAIR HAS EXACTLY ONE IMPLEMENTATION.** Two
 * routes now carry this same override grammar under two different id keys —
 * `dealLineId` on the deal conversions, `quoteProductId` on
 * `POST /quotes/{id}/convert` — and the header of this module says what a second
 * copy would cost: the two agree the day they are written and drift on the first
 * correction, and **the thing that would drift is the pair below**, whose halves
 * are mutually exclusive columns on a document that demands money.
 * {@link convertLineOverrideOf} and `quoteConvertLineOverrideOf` are therefore
 * both one line over this function, each stamping only its own key.
 */
export function convertLineChanges(
  edit: ConvertLineEdit,
  seed: ConvertLineEdit,
): Omit<DealConvertLineOverride, 'dealLineId'> | null {
  if (convertLineEditsMatch(edit, seed)) return null;

  const body: Omit<DealConvertLineOverride, 'dealLineId'> = {};

  if (edit.quantity !== seed.quantity) body.quantity = Number(edit.quantity.trim());
  if (edit.unitPrice !== seed.unitPrice && edit.unitPrice.trim() !== '') {
    body.unitPrice = edit.unitPrice.trim();
  }

  /*
   * ⚠️ **THE PAIR, ALWAYS, AND THIS IS THE WHOLE SAFETY ARGUMENT.** Both keys
   * are assigned inside ONE `if`, so there is no path that sets one and leaves
   * the other stored — and a request that sets one while the other stands IS
   * the 422. Read structurally rather than case by case: whatever
   * {@link convertDiscountWrite} returns, one half is inert by construction
   * (`discountAmount: null` in percentage mode, `discount: '0'` in amount
   * mode), so a row stating two live discounts cannot be built from here.
   *
   * ⚠️ **The comparison is STRING equality on both sides, deliberately, and it
   * over-reports rather than under-reports.** A stored `'0.00'` and a resolved
   * `'0'` are the same figure and different strings, so flicking the mode
   * toggle on an undiscounted line sends `discount: '0'` — a key that changes
   * nothing. That is the harmless direction. The alternative is comparing money
   * numerically, which is arithmetic on a decimal string, which is the one
   * thing nothing in this app does outside `@/lib/decimal`.
   */
  const money = convertDiscountWrite(edit);
  const seedMoney = convertDiscountWrite(seed);
  if (money.discount !== seedMoney.discount || money.discountAmount !== seedMoney.discountAmount) {
    body.discount = money.discount;
    body.discountAmount = money.discountAmount;
  }

  if (edit.description !== seed.description) {
    /*
     * ⚠️ An emptied box is `null`, which is DERIVE, not "print nothing". That
     * is the contract this field carries on all three line tables: a string is
     * verbatim, `null` derives from the product and the line's cities, and an
     * empty string is not a third state.
     */
    const description = edit.description.trim();
    body.description = description === '' ? null : description;
  }

  /*
   * A change that resolves to no key at all — clearing a price the line never
   * had, say — is not an override. Sending the id alone would be a row named in
   * a request that says nothing about it.
   *
   * ⚠️ **`> 0`, because the id key is no longer in this object.** It was
   * `Object.keys(body).length > 1` while `dealLineId` was assigned above; the
   * caller stamps the id now, so counting one key here would discard every
   * single-field edit — a typed quantity silently not sent, with a 201 and the
   * old figure on the invoice.
   */
  return Object.keys(body).length > 0 ? body : null;
}

/**
 * Every changed row, in the order the lines were read.
 *
 * ⚠️ **The INDEX of an entry is what a server refusal names** —
 * `lines.<n>.quantity` — so the caller has to keep this order to route a 422
 * back onto the row that caused it. Returning the array rather than a map is
 * what makes that index a fact rather than a guess.
 *
 * ⚠️ **An empty result means "send no `lines` key at all"**, and the caller
 * omits it. See the header: an empty array is a different instruction.
 */
export function convertLineOverrides(
  lines: readonly DealProduct[],
  edits: Readonly<Record<number, ConvertLineEdit>>,
): DealConvertLineOverride[] {
  const overrides: DealConvertLineOverride[] = [];
  for (const line of lines) {
    const edit = edits[line.id];
    if (edit === undefined) continue;
    const override = convertLineOverrideOf(line.id, edit, convertLineEditOf(line));
    if (override !== null) overrides.push(override);
  }
  return overrides;
}

/**
 * `lines.<index>` or `lines.<index>.<field>`, and nothing else.
 *
 * Exported because `POST /quotes/{id}/convert` names its refusals under the
 * SAME `lines` prefix — one grammar, two routes — so the quote router reuses
 * this rather than declaring a second regex that agrees today.
 */
export const CONVERT_LINE_PATH_PATTERN = /^lines\.(\d+)(?:\.([A-Za-z]+))?$/;

export const CONVERT_LINE_FIELDS: readonly ConvertLineField[] = [
  'quantity',
  'unitPrice',
  'discount',
  'discountAmount',
  'description',
];

/**
 * A server refusal, put back on the control that caused it.
 *
 * ⚠️ **The array INDEX is the only link between a refusal and a row.** The
 * server names `lines.2.quantity`, and `2` is a position in the array that was
 * sent — not a `dealLineId` and not a position in the deal's own line list,
 * which is longer because unchanged rows are omitted. So the same `overrides`
 * array that was sent has to be handed back here; anything else silently marks
 * the wrong row, with a `200`-shaped confidence and a red box on a figure
 * somebody never touched.
 *
 * ⚠️ **Anything that does not bind to a control comes back as `unrouted`, to be
 * BANNERED rather than dropped.** That covers a refusal on `dealLineId` — which
 * is ours to get right and has no box on any screen — an index the array does
 * not have, and a field name this branch has not been taught about. The
 * alternative is a form that refuses to save and explains itself nowhere
 * visible, which is the exact failure `routeFieldErrors` exists to prevent.
 */
export function convertLineFieldErrors(
  fieldErrors: Readonly<Record<string, string>>,
  overrides: readonly DealConvertLineOverride[],
): {
  rows: Record<number, Partial<Record<ConvertLineField, string>>>;
  unrouted: string[];
} {
  const rows: Record<number, Partial<Record<ConvertLineField, string>>> = {};
  const unrouted: string[] = [];

  for (const [path, message] of Object.entries(fieldErrors)) {
    const match = CONVERT_LINE_PATH_PATTERN.exec(path);
    if (match === null) continue;

    const override = overrides[Number(match[1])];
    const field = match[2];
    if (
      override === undefined ||
      field === undefined ||
      !CONVERT_LINE_FIELDS.includes(field as ConvertLineField)
    ) {
      unrouted.push(message);
      continue;
    }

    const row = rows[override.dealLineId] ?? {};
    row[field as ConvertLineField] = message;
    rows[override.dealLineId] = row;
  }

  return { rows, unrouted };
}

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * `lineSet` — THE COMPLETE FINAL LIST, and the SECOND key on the same route
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **`lines` above is UNCHANGED and is still what the INVOICE route sends.**
 * Everything below is additive. `POST /deals/{id}/convert` now accepts EITHER
 * `lines` (the partial override form) OR `lineSet` (this one), and **sending
 * both is a 422**.
 *
 * ⚠️ **WHY THE ROUTE HAS TWO KEYS AT ALL, AND WHY NEITHER MAY BE "SIMPLIFIED"
 * AWAY.** The deployed page sends partial `lines`. Had `lines` been redefined
 * as a complete list instead of a second key being added, then for the whole
 * gap between the two deploys every conversion would have silently DROPPED the
 * lines nobody edited — on a document that goes to a customer, with a 201 and
 * no error anywhere. The split is what makes the deploy safe, not tidiness.
 *
 * ## What `lineSet` MEANS, and it is the opposite instruction from `lines`
 *
 * | key | an entry names | a line NOT named |
 * | --- | --- | --- |
 * | `lines` | a respelling of a deal line | **is still copied** |
 * | `lineSet` | one line of the FINAL quote | **does not appear on the quote** |
 *
 * So an omission is a DELETION here and a no-op there. Two arrays of the same
 * shape, one word apart in the body, with opposite consequences for the rows
 * nobody touched — which is exactly why {@link DealConvertInput} declares them
 * as separate optional keys rather than one key with a mode flag.
 *
 * - an entry **with** `dealLineId` carries that deal line over, inheriting its
 *   cities, campaign inclusions and regions unless it states its own;
 * - an entry **without** one is brand new and **must** state `productId`;
 * - neither key present copies every deal line unchanged, which is what every
 *   conversion did before any of this existed.
 *
 * ⚠️ **THERE IS STILL NO `taxRate` AND NO `xeroTaxType`, ON EITHER KEY.**
 * Sending one is a 422 naming it. A converted line's rate is seeded from the
 * COMPANY inside the conversion, so the grid's tax cell stays a readout: a
 * control that accepts a value the request cannot carry is a silent discard.
 */

/** One campaign inclusion on a line, exactly as the line-item POST carries it. */
export interface ConvertLineInclusionWrite {
  inclusionId: number;
  /** Omitted means 1. An explicit 0 is refused by zod AND by a CHECK. */
  quantity?: number;
  sortOrder: number;
}

/**
 * One line of the quote a conversion is about to create.
 *
 * ⚠️ **Every key is optional INCLUDING `dealLineId` and `productId`, and the
 * pair is what decides which of two entirely different things this is.** A
 * carried-over line names `dealLineId`; a new one names `productId` and no
 * `dealLineId`. {@link convertLineSetEntryOf} is the only builder, so that
 * choice is made once rather than at a call site.
 *
 * ⚠️ **Money is a STRING and never a number**, and `quantity` is the one real
 * JSON number — the schema does not coerce, so a quoted quantity is a 422 on it
 * and a bare number is a 422 on the other three. Same rule as
 * {@link DealConvertLineOverride}.
 */
export interface DealConvertLineSetEntry {
  /** `f_deal_products.id` — the DEAL LINE's id. Absent means a brand-new line. */
  dealLineId?: number;
  /** REQUIRED on a new entry — a line with nothing to inherit from. */
  productId?: number;
  /** `null` CLEARS the project a carried-over line came with. */
  projectId?: number | null;
  cityIds?: number[];
  regionIds?: number[];
  inclusions?: ConvertLineInclusionWrite[];
  quantity?: number;
  unitPrice?: string;
  /** A percentage, as typed. Travels with the amount below, always. */
  discount?: string;
  /** An exact sum, or `null` for "this line discounts by percentage". */
  discountAmount?: string | null;
  /** Verbatim wording, or `null` to go back to DERIVING it server-side. */
  description?: string | null;
  /** Internal line note; absent copies the deal line's, `null` clears it. Never sent to Xero. */
  notes?: string | null;
  /**
   * The line's price tier (migration 328). Absent copies the deal line's tier
   * (or the default tier on a new line); `null` is the default tier.
   */
  priceTierId?: number | null;
  /** The row's position in the grid. The order the customer's document prints in. */
  sortOrder?: number;
}

/**
 * One row's boxes AND its structure — everything `lineSet` can carry.
 *
 * ⚠️ **This extends {@link ConvertLineEdit} rather than replacing it**, because
 * the five figures are judged and resolved by the same functions on both keys —
 * {@link validateConvertLineEdit} and, above all,
 * {@link convertDiscountWrite}, which is the one thing here whose two halves
 * are mutually exclusive columns on a customer-facing document.
 *
 * `productId: null` is *no product chosen yet*, which is a state a new row
 * starts in and a state the server will not accept — see
 * {@link convertLineSetBlocker}.
 */
export interface ConvertLineShape extends ConvertLineEdit {
  productId: number | null;
  projectId: number | null;
  cityIds: readonly number[];
  regionIds: readonly number[];
  inclusions: readonly ConvertLineInclusionWrite[];
  /**
   * The row's price tier (migration 328): `null` is the default tier.
   * ⚠️ **`undefined` means the caller does not track the tier**, and then no
   * `priceTierId` key is ever sent, so the server copies the deal line's tier.
   * A caller that tracks it must set it on the seed AND the draft.
   */
  priceTierId?: number | null;
}

/** Order-sensitive: the array's order IS the stored `sortOrder`. */
function idsKey(ids: readonly number[]): string {
  return ids.join(',');
}

/** Order-sensitive for the same reason, and it includes the per-entry count. */
function inclusionsWriteKey(entries: readonly ConvertLineInclusionWrite[]): string {
  return entries.map((entry) => `${entry.inclusionId}:${entry.quantity ?? ''}`).join('|');
}

/**
 * Has anything on this row moved away from the deal line it was seeded from?
 *
 * ⚠️ **ONE change test, and it is the same one {@link convertLineSetEntryOf}
 * uses to decide which keys to send.** A second rule for "has this moved" is
 * exactly how a row comes to read as unchanged on screen while a key for it is
 * on the wire.
 */
export function convertLineShapesMatch(a: ConvertLineShape, b: ConvertLineShape): boolean {
  return (
    convertLineEditsMatch(a, b) &&
    a.productId === b.productId &&
    a.projectId === b.projectId &&
    idsKey(a.cityIds) === idsKey(b.cityIds) &&
    idsKey(a.regionIds) === idsKey(b.regionIds) &&
    inclusionsWriteKey(a.inclusions) === inclusionsWriteKey(b.inclusions) &&
    (a.priceTierId ?? null) === (b.priceTierId ?? null)
  );
}

/**
 * One row, as the entry `lineSet` carries.
 *
 * **A CARRIED-OVER ROW** (`seed !== null`) sends `dealLineId` plus only the keys
 * that actually MOVED. Everything else inherits — the server copies the deal
 * line's cities, inclusions and regions — so restating a stored value would be a
 * key the server can refuse for something the person never chose.
 *
 * **A NEW ROW** (`seed === null`) sends everything it states and omits
 * everything it does not, exactly as `createPayload` does for a blank quote:
 * there is nothing stored for an omission to mean *unchanged*, so omitting is
 * the honest way to say *nothing here*.
 *
 * ⚠️ **`sortOrder` is on EVERY entry, including an untouched one.** With rows
 * addable and removable the array's order is the only thing that says where a
 * line prints, and a carried-over line that has moved up the grid because the
 * one above it was deleted has moved on the customer's document too.
 *
 * ⚠️ **THE DISCOUNT PAIR TRAVELS TOGETHER, ALWAYS, AND THIS IS THE WHOLE SAFETY
 * ARGUMENT.** Both keys are assigned inside ONE branch, so there is no path that
 * sets one and leaves the other stored — and a request that sets one while the
 * other stands IS the 422. Read structurally rather than case by case: whatever
 * {@link convertDiscountWrite} returns, one half is inert by construction
 * (`discountAmount: null` in percentage mode, `discount: '0'` in amount mode),
 * so a row stating two live discounts cannot be built from here.
 */
export function convertLineSetEntryOf(
  shape: ConvertLineShape,
  seed: ConvertLineShape | null,
  dealLineId: number | null,
  sortOrder: number,
): DealConvertLineSetEntry {
  const entry: DealConvertLineSetEntry = {};
  if (dealLineId !== null) entry.dealLineId = dealLineId;

  const money = convertDiscountWrite(shape);

  if (seed === null) {
    /*
     * ⚠️ **A NEW ENTRY WITHOUT A PRODUCT IS UNSENDABLE**, and the caller is
     * required to have refused it already — see {@link convertLineSetBlocker}.
     * `productId` is written whenever one exists so a new line can never be
     * silently emitted as an inherit-everything entry keyed to nothing.
     */
    if (shape.productId !== null) entry.productId = shape.productId;
    if (shape.projectId !== null) entry.projectId = shape.projectId;
    if (shape.cityIds.length > 0) entry.cityIds = [...shape.cityIds];
    if (shape.regionIds.length > 0) entry.regionIds = [...shape.regionIds];
    if (shape.inclusions.length > 0) entry.inclusions = shape.inclusions.map((one) => ({ ...one }));
    /*
     * Omitted for the default tier: a new line with no `priceTierId` is the
     * default tier, and with no `unitPrice` the server prices it.
     */
    if (shape.priceTierId !== undefined && shape.priceTierId !== null) {
      entry.priceTierId = shape.priceTierId;
    }
    if (shape.quantity.trim() !== '') entry.quantity = Number(shape.quantity.trim());
    if (shape.unitPrice.trim() !== '') entry.unitPrice = shape.unitPrice.trim();
    /*
     * Omitted when it says nothing. A `0` percentage with no amount is what an
     * untouched percentage box AND an emptied amount box both resolve to, and
     * both mean "this line is not discounted" — the server's own default, so
     * saying it is a key that changes nothing.
     */
    if (money.discount !== '0' || money.discountAmount !== null) {
      entry.discount = money.discount;
      entry.discountAmount = money.discountAmount;
    }
    const description = shape.description.trim();
    if (description !== '') entry.description = description;
    entry.sortOrder = sortOrder;
    return entry;
  }

  if (shape.productId !== seed.productId && shape.productId !== null) {
    entry.productId = shape.productId;
  }
  /*
   * ⚠️ **`null` is sent, and it is the one place an explicit null appears on
   * this key.** Clearing a carried-over line's project is a real edit and an
   * omission would be read as *inherit*, so the two cannot share a spelling.
   */
  if (shape.projectId !== seed.projectId) entry.projectId = shape.projectId;
  /*
   * ⚠️ **`[]` CLEARS, and an omission INHERITS.** Same shape as `cityIds` and
   * `inclusions` everywhere else in this API, and the reason the test is on the
   * fingerprint rather than on the length: a length test silently turns "remove
   * every city from this line" into "leave the deal's cities alone".
   */
  if (idsKey(shape.cityIds) !== idsKey(seed.cityIds)) entry.cityIds = [...shape.cityIds];
  if (idsKey(shape.regionIds) !== idsKey(seed.regionIds)) entry.regionIds = [...shape.regionIds];
  if (inclusionsWriteKey(shape.inclusions) !== inclusionsWriteKey(seed.inclusions)) {
    entry.inclusions = shape.inclusions.map((one) => ({ ...one }));
  }
  /*
   * Only a tier that MOVED, and only from a caller that tracks it: an omission
   * copies the deal line's tier, `null` returns the line to the default tier.
   */
  if (shape.priceTierId !== undefined && shape.priceTierId !== (seed.priceTierId ?? null)) {
    entry.priceTierId = shape.priceTierId;
  }
  if (shape.quantity !== seed.quantity && shape.quantity.trim() !== '') {
    entry.quantity = Number(shape.quantity.trim());
  }
  if (shape.unitPrice !== seed.unitPrice && shape.unitPrice.trim() !== '') {
    entry.unitPrice = shape.unitPrice.trim();
  }
  const seedMoney = convertDiscountWrite(seed);
  if (money.discount !== seedMoney.discount || money.discountAmount !== seedMoney.discountAmount) {
    entry.discount = money.discount;
    entry.discountAmount = money.discountAmount;
  }
  if (shape.description !== seed.description) {
    /*
     * ⚠️ An emptied box is `null`, which is DERIVE, not "print nothing" — the
     * contract this field carries on all three line tables.
     */
    const description = shape.description.trim();
    entry.description = description === '' ? null : description;
  }
  entry.sortOrder = sortOrder;
  return entry;
}

/** `''` and whitespace-only are "no note"; the server folds them to `null` too. */
function lineNoteOf(value: string | null | undefined): string | null {
  return value === undefined || value === null || value.trim() === '' ? null : value.trim();
}

/**
 * The `notes` key one convert entry carries (owner 2026-10-18), or `undefined`
 * for no key at all. Serves all three convert paths: `lineSet` on both deal
 * routes and `lines` on `POST /quotes/{id}/convert`.
 *
 * ⚠️ **The note rides OUTSIDE {@link ConvertLineShape}, deliberately.** The
 * server copies a carried-over line's note by itself, so the key goes out only
 * when it MOVED, and a note-only edit must not mark the row changed, which
 * would put an untouched legacy line through the grid's full validator.
 *
 * `source` is the line the row was copied from, or `null` for a row added on
 * the convert screen (which sends a note only when it has one). An `undefined`
 * source is an older API that does not report notes and reads as none.
 * `null` on the wire CLEARS the copied note.
 */
export function convertLineNotesWrite(
  draft: string | null,
  source: string | null | undefined,
): string | null | undefined {
  const next = lineNoteOf(draft);
  return next === lineNoteOf(source) ? undefined : next;
}

/**
 * Whether this entry says anything at all beyond *carry this line over as it
 * is, in this position*.
 *
 * ⚠️ **Read off the BUILT entry rather than recomputed**, so "the set is
 * untouched" and "the request says nothing" cannot disagree. It is what lets the
 * caller omit `lineSet` entirely on a conversion nobody edited — and an omitted
 * key is provably today's behaviour, where an array restating every line is a
 * request shape nobody has ever exercised.
 */
export function convertLineSetEntryIsCarryOver(entry: DealConvertLineSetEntry): boolean {
  if (entry.dealLineId === undefined) return false;
  for (const key of Object.keys(entry)) {
    if (key !== 'dealLineId' && key !== 'sortOrder') return false;
  }
  return true;
}

/*
 * ⚠️ **A NEW ENTRY MUST STATE A `productId`, AND THERE IS DELIBERATELY NO
 * VALIDATOR FOR THAT RULE HERE.**
 *
 * A `convertLineSetBlocker(shape, dealLineId)` returning *"Choose a product."*
 * was written and DELETED before it shipped. The grid that calls
 * {@link convertLineSetEntryOf} already runs its own `validateRow` over every
 * row it is about to send, and that function marks `productId` with the
 * identical sentence for the identical condition — so a copy here would be a
 * SECOND mechanism deciding one thing, and the one that drifts is always the one
 * nobody was looking at. Everything else a row can be refused for — the
 * percentage bound, the city set against the project, the duplicate
 * product-and-project pair — lives there too, for the same reason.
 *
 * What the caller owes, stated because the type cannot enforce it: **do not pass
 * a row with `dealLineId === null` and `productId === null`.**
 * {@link convertLineSetEntryOf} will happily build the entry — an
 * inherit-everything entry keyed to nothing — and the server answers a 422 at
 * `lineSet.<n>.productId`, which is exactly the refusal the person should have
 * been told about on the field.
 */

/** `lineSet.<index>` or `lineSet.<index>.<field>`, and nothing else. */
const LINE_SET_PATH_PATTERN = /^lineSet\.(\d+)(?:\.([A-Za-z]+))?$/;

/**
 * The refusal fields that have a control to land on.
 *
 * ⚠️ **`dealLineId` and `sortOrder` are deliberately ABSENT.** Both are ours to
 * get right and neither has a box on any screen, so a refusal on one is
 * `unrouted` and gets BANNERED — the alternative is a form that refuses to
 * convert and explains itself nowhere visible.
 */
const CONVERT_LINE_SET_FIELDS: readonly string[] = [
  'productId',
  'projectId',
  'cityIds',
  'allCities',
  'regionIds',
  'inclusions',
  'priceTierId',
  'quantity',
  'unitPrice',
  'discount',
  'discountAmount',
  'description',
];

/**
 * A server refusal on `lineSet`, put back on the control that caused it.
 *
 * ⚠️ **THE INDEX IS AN ARRAY POSITION, NOT A `dealLineId`.** That is the one
 * thing that changed from {@link convertLineFieldErrors}, and it changed because
 * `lineSet` carries rows that have no `dealLineId` at all — a new line cannot be
 * addressed by an id it does not have. So the caller hands back the `rowKeys`
 * array the grid emitted ALONGSIDE the entries: same length, same order, built
 * in the same pass, which is what makes position a fact rather than a guess.
 *
 * ⚠️ **Hand back the array that was SENT.** Reading the current one would work
 * today and would mark the wrong row the first time a keystroke landed between
 * the request and its refusal — with a 200-shaped confidence and a red box on a
 * figure somebody never touched.
 */
export function convertLineSetFieldErrors(
  fieldErrors: Readonly<Record<string, string>>,
  rowKeys: readonly string[],
): { rows: Record<string, Record<string, string>>; unrouted: string[] } {
  const rows: Record<string, Record<string, string>> = {};
  const unrouted: string[] = [];

  for (const [path, message] of Object.entries(fieldErrors)) {
    const match = LINE_SET_PATH_PATTERN.exec(path);
    if (match === null) continue;

    const rowKey = rowKeys[Number(match[1])];
    const field = match[2];
    if (rowKey === undefined || field === undefined || !CONVERT_LINE_SET_FIELDS.includes(field)) {
      unrouted.push(message);
      continue;
    }

    const row = rows[rowKey] ?? {};
    row[field] = message;
    rows[rowKey] = row;
  }

  return { rows, unrouted };
}
